import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  Optional,
  ElementRef,
  ViewChild
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  DATE_FORMAT_YMD_HM,
  DATE_FORMAT_YMD,
  Translatable,
  TranslationService,
  DateFormatService
} from '@ngmedax/translation';
import {WorkflowInstance, Translation, Questionnaire, Survey, Submission} from '@ngmedax/common-questionnaire-types';
import {QrCodeModalComponent} from './modals/qrcode-modal.component';
import {LayoutService, EventService} from '@ngmedax/layout';
import {ConfigService} from '@ngmedax/config';
import {PermissionService} from '@ngmedax/permission';
import {Router} from '@angular/router';
import {TRANSLATION_GRID_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {WorkflowService} from '../services/workflow.service';
import {SurveyResult} from '../../../types';
import {MediaCenterService} from '@ngmedax/asset';
import {permission} from '../workflow.permission';

/** A template step paired with its recorded survey, in execution order. */
interface WorkflowHistoryStage {
  position: number;
  name: Translation;
  questionnaires: string[];
  survey: Survey;
  entries: WorkflowHistoryQuestionnaire[];
}

/** A questionnaire with all of its recorded downloads. */
interface WorkflowHistoryQuestionnaire {
  id: string;
  questionnaire?: Questionnaire;
  submissions: Submission[];
}

/** Loaded instance with a stable presentation of all planned and recorded steps. */
interface WorkflowHistoryInstance extends WorkflowInstance {
  historyStages: WorkflowHistoryStage[];
}

export interface WorkflowInstanceGridComponent extends Translatable {}

@Component({
  selector: 'app-workflow-instance-grid',
  templateUrl: './workflow-instance-grid.component.html',
  styleUrls: ['./workflow-instance-grid.component.css', './workflow-stages.css']
})
@Translatable({ scope: TRANSLATION_GRID_SCOPE, keys: KEYS })
export class WorkflowInstanceGridComponent implements OnInit, OnDestroy {
  public isSearchCollapsed = true;
  public isGridCollapsed = false;
  public gridPageNumber = 1;
  public displayPerPage = 25;
  public workflowInstances: WorkflowHistoryInstance[] = [];
  public filter: {
    workflow?: string;
    status?: string;
    externStatus?: string;
    anonymous?: string;
    sort?: string;
    order?: string;
    questionnaires?: string[];
  } = {
    workflow: '',
    status: '',
    externStatus: '',
    anonymous: '1',
    sort: 'updatedAt',
    order: 'desc',
    questionnaires: []
  };
  public clientFilter: {[key: string]: any} = {};
  public customFilter: {[key: string]: any} = {};

  // Auto-refresh properties
  public autoRefreshInterval = 0; // 0 means disabled
  public autoRefreshOptions = [
    { value: 0, label: '' },
    { value: 15, label: '15 Sek' },
    { value: 30, label: '30 Sek' },
    { value: 60, label: '1 Min' },
    { value: 120, label: '2 Min' }
  ];
  private autoRefreshTimer: any = null;
  private isQrCodeModalOpen = false; // Track if QR code modal is open

  public expandedInstances = new Set<string>();
  public total = 0;
  public locale = 'de_DE';
  public defaultLocale = 'de_DE';
  public canEditPatient = false;
  private filterChangeTimeout: any = null;
  private showPreloaderTimeout: any = null;
  private assetsBaseUri = '';

  public questionnaireTitles: { [uid: string]: Translation } = {};

  /**
   * Loaded questionnaires by their uid (full objects, needed by the origin renderer)
   */
  public questionnaireById: { [uid: string]: Questionnaire } = {};

  public questionnaires: Questionnaire[] = [];

  public get dateFormat() {
    const format = DATE_FORMAT_YMD_HM.replace(/YYYY/, 'YY');
    return this.getDateFormat(format);
  }

  /** Shows the complete year in expanded history timestamps. */
  public get detailDateFormat() {
    return this.getDateFormat(DATE_FORMAT_YMD_HM);
  }

  constructor(
    @Optional() private translationService: TranslationService,
    @Optional() private dateFormatService: DateFormatService,
    @Optional() private permissionService: PermissionService,
    private layoutService: LayoutService,
    private eventService: EventService,
    private workflowService: WorkflowService,
    private ref: ChangeDetectorRef,
    private configService: ConfigService,
    private router: Router,
    private modalService: NgbModal,
    @Optional() private mediaCenterService: MediaCenterService
  ) {
    this.assetsBaseUri = this.configService.get('apis.submission.assets.cdn');
  }

  ngOnInit(): void {
    this.layoutService.showPreloader();
    this.loadWorkflowInstances();
    this.loadQuestionnaires();

    if (this.permissionService) {
      const routeData = {
        needsLogin: true,
        needsPermissions: [
          permission.MYMEDAX_PATIENT_UPDATE,
          permission.MYMEDAX_PATIENT_QUESTIONNAIRE_READ,
          permission.MYMEDAX_PATIENT_QUESTIONNAIRE_ASSIGN,
          permission.MYMEDAX_PATIENT_QUESTIONNAIRE_UNASSIGN
        ]
      };

      const result = this.permissionService.isAllowedByRouteData(routeData);
      this.canEditPatient = !!result.allowed;
    }
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  /**
   * Starts or stops auto-refresh based on selected interval
   */
  public onAutoRefreshChange(): void {
    this.stopAutoRefresh();

    if (this.autoRefreshInterval > 0) {
      this.autoRefreshTimer = setInterval(() => {
        // only refresh if QR code modal is not open
        !this.isQrCodeModalOpen && this.onRefresh();
      }, this.autoRefreshInterval * 1000);
    }
  }

  /**
   * Stops the auto-refresh timer if active
   */
  private stopAutoRefresh(): void {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
    }
  }

  public async onRefresh() {
    this.showPreloader();
    await new Promise(resolve => setTimeout(resolve, 500));
    this.loadWorkflowInstances();
  }

  public onPagingChange() {
    this.ref.detectChanges();
    this.ref.markForCheck();
    this.loadWorkflowInstances();
  }

  public onFilterChange() {
    clearTimeout(this.filterChangeTimeout);
    this.filterChangeTimeout = setTimeout(() => {
      this.loadWorkflowInstances();
    }, 1000);
  }

  public onSortChange(col: string) {
    if (this.filter.sort === col) {
      this.filter.order = this.filter.order === 'asc' ? 'desc' : 'asc';
    } else {
      this.filter.sort = col;
      this.filter.order = 'asc';
    }
    this.loadWorkflowInstances();
  }

  public async onContinueWorkflow(instance: WorkflowInstance) {
    if (!instance || !instance.clientId || !instance.id) {
      alert(this._(KEYS.GRID.FOUND_NO_ENTRY_BY_ID));
      return;
    }
    try {
      this.layoutService.showPreloader();
      const result: SurveyResult = await this.workflowService.continueWorkflow(
        instance.clientId,
        instance.id
      );
      this.layoutService.hidePreloader();
      if (result && result.link) {
        window.open(result.link, '_blank');
      } else {
        alert(this._(KEYS.GRID.ERROR_LOADING_WORKFLOWS));
      }
    } catch (error) {
      this.layoutService.hidePreloader();
      console.error(error);
      alert(this._(KEYS.GRID.ERROR_LOADING_WORKFLOWS));
    }
  }

  public async onCopyWorkflowContinueLink(instance: WorkflowInstance) {
    if (!instance || !instance.clientId || !instance.id) {
      alert(this._(KEYS.GRID.FOUND_NO_ENTRY_BY_ID));
      return;
    }
    const result: SurveyResult = await this.workflowService.continueWorkflow(
      instance.clientId,
      instance.id
    );
    const url = result && result.link;
    navigator.clipboard
      .writeText(url)
      .then(() => alert(this._(KEYS.GRID.COPY_LINK_SUCCESS)))
      .catch(() => alert(this._(KEYS.GRID.COPY_LINK_ERROR)));
  }

  public async onShowQRCode(instance: WorkflowInstance) {
    if (!instance || !instance.clientId || !instance.id) {
      alert(this._(KEYS.GRID.FOUND_NO_ENTRY_BY_ID));
      return;
    }
    try {
      this.layoutService.showPreloader();
      const result: SurveyResult = await this.workflowService.continueWorkflow(
        instance.clientId,
        instance.id
      );
      this.layoutService.hidePreloader();
      
      // set flag that modal is open
      this.isQrCodeModalOpen = true;
      
      const modalRef = this.modalService.open(QrCodeModalComponent, {
        size: 'lg',
        centered: true
      });

      let qrCode = result.qrCode.assign;

      if (!qrCode.includes('&locale=')) {
        qrCode += `&locale=${result.survey.locale || this.defaultLocale}`;
      }

      modalRef.componentInstance.qrCodeString = qrCode;
      result.link && (modalRef.componentInstance.qrCodeLink = result.link);
      
      // set flag back to false when modal is closed (handles both close and dismiss)
      modalRef.result.finally(() => this.isQrCodeModalOpen = false);
    } catch (error) {
      this.isQrCodeModalOpen = false; // in case of error, ensure flag is reset
      this.layoutService.hidePreloader();
      console.error(error);
      alert(this._(KEYS.GRID.ERROR_LOADING_QR_CODE));
    }
  }

  public fromMySQLDate(date: string): string {
    return this.dateFormatService && date
      ? this.dateFormatService.fromMySqlDate(date, this.getDateFormat(DATE_FORMAT_YMD))
      : date;
  }

  private loadWorkflowInstances() {
    this.showPreloader();
    const filter = this.getQueryFilter();
    const opts = this.getQueryOpts();
    this.workflowService
      .loadWorkflowInstances(filter, opts)
      .then(result => {
        this.workflowInstances = result.rows.map(instance => ({
          ...instance,
          historyStages: this.buildHistoryStages(instance)
        }));
        this.total = result.total;
        this.hidePreloader();
      })
      .catch(error => {
        this.hidePreloader();
        alert(this._(KEYS.GRID.ERROR_LOADING_WORKFLOWS));
        console.log(error);
      });
  }

  private loadQuestionnaires() {
    this.workflowService
      .loadQuestionnaires()
      .then(questionnaires => {
        if (!questionnaires) {
          return;
        }
        this.questionnaires = questionnaires;

        for (const q of questionnaires) {
          this.questionnaireTitles[q.id] = q.meta.title;
          this.questionnaireById[q.id] = q;
        }
      })
      .catch(error => {
        console.error(error);
      });
  }

  /**
   * Returns true if a workflow instance is expired
   */
  public isExpired(instance: WorkflowInstance): boolean {
    const now = <any>new Date().toJSON();
    const isDone = instance.status === 'submitted' || instance.status === 'canceled';
    const isExpired = instance.validUntil && instance.validUntil < now;

    if (isDone) {
      return false;
    }

    return isExpired;
  }

  /**
   * Name of the workflow of an instance in the display locale, safe against
   * missing workflow or name (api and import may leave them empty).
   */
  public getWorkflowName(instance: any): string {
    const name = (instance && instance.workflow && instance.workflow.name) || {};
    return name[this.locale] || name[this.defaultLocale] || '?';
  }

  /** Builds steps without dropping surveys when a template is missing or shorter. */
  public buildHistoryStages(instance: WorkflowInstance): WorkflowHistoryStage[] {
    const stages = instance.workflow && instance.workflow.stages || [];
    const surveys = instance.surveys || [];
    return Array.from({length: Math.max(stages.length, surveys.length)}, (_, position) => {
      const stage = stages[position];
      const survey = surveys[position];
      const questionnaires = stage ? stage.questionnaires || [] : (survey && survey.questionnaires || [])
        .map(questionnaire => typeof questionnaire === 'string' ? questionnaire : questionnaire && questionnaire.id)
        .filter(id => !!id);
      const entries: WorkflowHistoryQuestionnaire[] = Array.from(new Set(questionnaires))
        .map(id => ({id, submissions: []}));
      for (const submission of survey && survey.submissions || []) {
        if (!submission) {
          continue;
        }
        const questionnaire = submission.questionnaire;
        const id = questionnaire && questionnaire.id;
        let entry = id && entries.find(item => item.id === id);
        if (!entry) {
          entry = {id: id || '', questionnaire, submissions: []};
          entries.push(entry);
        }
        if (questionnaire && questionnaire.meta) {
          entry.questionnaire = questionnaire;
        }
        entry.submissions.push(submission);
      }
      return {position, name: stage && stage.name || {}, questionnaires, entries, survey};
    });
  }

  /** Looks up a recorded questionnaire before falling back to the current catalog. */
  public getStageQuestionnaire(stage: WorkflowHistoryStage, id: string): Questionnaire {
    const recorded = stage.survey && stage.survey.questionnaires || [];
    return recorded.find(questionnaire => questionnaire && typeof questionnaire === 'object'
      && questionnaire.id === id && questionnaire.meta) || this.questionnaireById[id];
  }

  /** Keeps expansion stable when auto-refresh replaces instance objects. */
  public instanceKey(instance: WorkflowInstance): string {
    return instance.id || instance.uid;
  }

  /** Reuses rendered rows during refresh to retain focus on their controls. */
  public trackInstance(index: number, instance: WorkflowInstance): string {
    return instance.id || instance.uid;
  }

  /** Expands only the requested instance without triggering other row actions. */
  public toggleHistory(instance: WorkflowInstance): void {
    const key = this.instanceKey(instance);
    this.expandedInstances.has(key) ? this.expandedInstances.delete(key) : this.expandedInstances.add(key);
  }

  /** Summarizes outcomes without counting skipped steps as completed. */
  public getHistorySummary(instance: WorkflowHistoryInstance): string {
    const counts = new Map<string, number>();
    for (const stage of instance.historyStages) {
      const status = this.getSurveyStatus(stage.survey);
      const label = status === this.historyStatuses.signed ? KEYS.GRID.STATUS_SUBMITTED : status.label;
      counts.set(label, (counts.get(label) || 0) + 1);
    }
    return Array.from(counts, ([label, count]) => count + ' ' + this._(label)).join(' · ');
  }

  /** Resolves the step title in the current display locale. */
  public getHistoryStageTitle(stage: WorkflowHistoryStage): string {
    return stage.name[this.locale] || stage.name[this.defaultLocale]
      || this._(KEYS.GRID.STAGE) + ' ' + (stage.position + 1);
  }

  /** Presentation metadata for survey and instance states. */
  public readonly historyStatuses = {
    notStarted: {label: KEYS.GRID.NOT_STARTED, icon: 'fa-circle-o', tone: 'muted'},
    created: {label: KEYS.GRID.STATUS_CREATED, icon: 'fa-plus', tone: 'muted'},
    assigned: {label: KEYS.GRID.STATUS_ASSIGNED, icon: 'fa-user', tone: 'active'},
    submitted: {label: KEYS.GRID.STATUS_SUBMITTED, icon: 'fa-check', tone: 'done'},
    signed: {label: KEYS.GRID.STATUS_SIGNED, icon: 'fa-pencil', tone: 'done'},
    canceled: {label: KEYS.GRID.STATUS_CANCELED, icon: 'fa-ban', tone: 'danger'},
    skipped: {label: KEYS.GRID.STATUS_SKIPPED, icon: 'fa-fast-forward', tone: 'muted'},
    expired: {label: KEYS.GRID.STATUS_EXPIRED, icon: 'fa-clock-o', tone: 'danger'},
    unknown: {label: KEYS.GRID.STATUS_UNKNOWN, icon: 'fa-question', tone: 'muted'}
  };

  /** Returns an explicit state for recorded surveys and future steps. */
  public getSurveyStatus(survey: Survey) {
    return survey ? this.historyStatuses[survey.status] || this.historyStatuses.unknown : this.historyStatuses.notStarted;
  }

  /** Keeps expiration and terminal states ahead of in-progress indicators. */
  public getInstanceStatus(instance: WorkflowInstance) {
    if (this.isExpired(instance)) {
      return this.historyStatuses.expired;
    }
    if (!instance.status || instance.status === 'created') {
      return instance.surveys && instance.surveys.length ? this.historyStatuses.assigned : this.historyStatuses.created;
    }
    return this.historyStatuses[instance.status] || this.historyStatuses.unknown;
  }

  public getQuestionnaireTitle(uid: string): string {
    if (this.questionnaireTitles[uid] && this.questionnaireTitles[uid][this.locale]) {
      return this.questionnaireTitles[uid][this.locale];
    }
    return '';
  }

  private getQueryFilter() {
    const filter: any = {};
    const like = (value: string) => ({ $regex: value, $options: 'i' });
    const equal = (value: string) => ({ $eq: value });
    const notEqual = (value: string|boolean) => ({ $ne: value });

    if (this.filter.status) {
      filter.status = equal(this.filter.status);
    }

    // standard fields from clientFilter
    for (const key of Object.keys(this.clientFilter)) {
      if (this.clientFilter[key]) {
        filter[`client.${key}`] = like(this.clientFilter[key]);
      }
    }

    // custom fields from customFilter
    for (const key of Object.keys(this.customFilter)) {
      if (this.customFilter[key]) {
        filter[`client.custom.${key}`] = like(this.customFilter[key]);
      }
    }

    // Special handling for externStatus "open" to include records without externStatus
    if (this.filter.externStatus) {
      if (this.filter.externStatus === 'open') {
        // Include both "open" and undefined/null values
        filter['$or'] = [
          { externStatus: equal('open') },
          { externStatus: { $exists: false } },
          { externStatus: null }
        ];
      } else {
        // For other values, use exact matching
        filter.externStatus = equal(this.filter.externStatus);
      }
    }

    if (this.filter.workflow) {
      const likeValue = like(this.filter.workflow);
      filter[`workflow.name.${this.locale}`] = likeValue;
    }

    if (this.filter.questionnaires && this.filter.questionnaires.length) {
      filter['workflow.stages.questionnaires'] = {
        $all: this.filter.questionnaires
      };
    }

    // Handle anonymous filter - same logic as patient grid
    if (this.filter.anonymous) {
      if (this.filter.anonymous == '1') {
        // Not anonymous/pseudonymous
        filter['client.anonymous'] = notEqual(true);
        filter['client.pseudonymous'] = notEqual(true);
      } else if (this.filter.anonymous == '2') {
        // Anonymous
        filter['client.anonymous'] = { $eq: true };
      } else if (this.filter.anonymous == '3') {
        // Pseudonymous
        filter['client.pseudonymous'] = { $eq: true };
      }
    }

    return filter;
  }

  private getQueryOpts() {
    const queryOpts = {
      limit: this.displayPerPage,
      offset: (this.gridPageNumber - 1) * this.displayPerPage,
      sort: ''
    };
    if (this.filter.sort) {
      queryOpts.sort = this.filter.order === 'asc' ? this.filter.sort : '-' + this.filter.sort;
    }
    return queryOpts;
  }

  private showPreloader() {
    this.showPreloaderTimeout = setTimeout(() => this.layoutService.showPreloader(), 200);
  }

  private hidePreloader() {
    clearTimeout(this.showPreloaderTimeout);
    this.layoutService.hidePreloader();
  }

  public hasValidExports(submission: any) {
    const exports = submission.exports;
    const isArray = Array.isArray(exports);
    const hasElements = isArray && exports.length > 0;
    const hasObjects = hasElements && typeof exports[0] === 'object';
    const hasExpectedFormat = hasObjects && !!exports[0].type && !!exports[0].fileName;
    return hasExpectedFormat;
  }

  /**
   * Returns true if submission has at least one downloadable (non extern) export
   */
  public hasDownloadableExports(submission: any): boolean {
    return this.hasValidExports(submission)
      && submission.exports.some((exp) => exp && exp.type && exp.type !== 'EXTERN' && exp.fileName);
  }

  /**
   * Returns true if the download dropdown has any content for given submission:
   * downloadable exports, the assets modal or an ownCloud link.
   * Used to only render the dropdown when it actually has entries.
   */
  public hasDownloadContent(submission: any): boolean {
    if (!submission) {
      return false;
    }

    const hasAssets = !!submission.bucketId;
    const hasOwnCloud = !!(submission.uploadLinks && submission.uploadLinks['ownCloud']);
    return hasAssets || hasOwnCloud || this.hasDownloadableExports(submission);
  }

  public onDownload(exportFileName: string) {
    this.assetsBaseUri && window.open(`${this.assetsBaseUri}/${exportFileName}`, '_blank');
  }

  public onOpenMediaCenterModal(submission: { bucketId?: string }) {
    this.mediaCenterService &&
    this.mediaCenterService.openModal({
      bucketId: submission.bucketId,
      disableUpload: true,
      enableZipDownload: true
    });
  }

  public onOpenLink(link: string) {
    link && window.open(link, '_blank');
  }

  public onExternStatusChange(instance: WorkflowInstance, status: string) {
    this.workflowService.updateWorkflowInstanceExternStatus(instance.id || instance._id, status).catch(error => {
      console.error(error);
      alert(this._(KEYS.GRID.ERROR_LOADING_WORKFLOWS));
    });
  }

  public _!: (text: string, variables?: any) => string;
  public getDateFormat!: (format: string, locale?: string) => string;
  public readonly KEYS = KEYS;
}
